import { cn } from "../../../lib/utils";
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Clock,
  Building2,
  Server,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  ExternalLink,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// WHOIS Module Data Types
interface Registrar {
  name?: string;
  iana_id?: number;
  url?: string;
  abuse_email?: string;
}

interface DomainInfo {
  tld?: string;
  registrar?: Registrar;
  creation_date?: string;
  expiry_date?: string;
  updated_date?: string;
  name_servers?: string[];
  status?: Array<string | Record<string, unknown>>;
  dnssec?: boolean;
  privacy_protected?: boolean;
}

export interface WhoisModuleData {
  domain_info?: DomainInfo;
  issues?: Array<{ code?: string; message?: string } | string | Issue>;
}

export interface WhoisModuleViewProps {
  data: WhoisModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Calculate days until date
function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// Calculate domain age in years
function domainAge(dateStr?: string): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const years = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365));
  if (years < 1) {
    const months = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 30));
    return `${months} month${months !== 1 ? "s" : ""}`;
  }
  return `${years} year${years !== 1 ? "s" : ""}`;
}

// Expiry countdown display
function ExpiryCountdown({ expiryDate }: { expiryDate?: string }) {
  const days = daysUntil(expiryDate);

  if (days === null) {
    return (
      <div className="p-6 rounded-lg bg-muted/30 border border-border text-center">
        <p className="text-muted-foreground">Expiry date unknown</p>
      </div>
    );
  }

  let color = "text-emerald-400";
  let bgColor = "bg-emerald-500/10";
  let borderColor = "border-emerald-500/30";
  let status = "Healthy";

  if (days <= 0) {
    color = "text-red-400";
    bgColor = "bg-red-500/10";
    borderColor = "border-red-500/30";
    status = "Expired";
  } else if (days <= 30) {
    color = "text-red-400";
    bgColor = "bg-red-500/10";
    borderColor = "border-red-500/30";
    status = "Critical";
  } else if (days <= 90) {
    color = "text-amber-400";
    bgColor = "bg-amber-500/10";
    borderColor = "border-amber-500/30";
    status = "Expiring Soon";
  }

  return (
    <div
      className={cn(
        "p-6 rounded-lg border text-center",
        bgColor,
        borderColor
      )}
    >
      <div className="flex items-center justify-center gap-2 mb-2">
        <Clock className={cn("h-5 w-5", color)} />
        <span className={cn("text-sm font-medium", color)}>{status}</span>
      </div>
      <p className={cn("text-4xl font-bold font-mono mb-1", color)}>
        {days <= 0 ? "0" : days}
      </p>
      <p className="text-sm text-muted-foreground">
        {days <= 0 ? "days expired" : "days remaining"}
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        Expires: {new Date(expiryDate!).toLocaleDateString()}
      </p>
    </div>
  );
}

// Registrar info card
function RegistrarCard({ registrar }: { registrar?: Registrar }) {
  if (!registrar) {
    return (
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Registrar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unknown</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Registrar
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="font-semibold">{registrar.name || "Unknown"}</p>
          {registrar.iana_id && (
            <p className="text-xs text-muted-foreground">
              IANA ID: {registrar.iana_id}
            </p>
          )}
        </div>
        {registrar.url && (
          <a
            href={registrar.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
          >
            Visit Website
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {registrar.abuse_email && (
          <p className="text-xs text-muted-foreground">
            Abuse: {registrar.abuse_email}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Status code badge with explanation
const STATUS_EXPLANATIONS: Record<string, { label: string; type: "good" | "warning" | "bad" | "info" }> = {
  ok: { label: "Domain is active", type: "good" },
  active: { label: "Domain is active", type: "good" },
  clientTransferProhibited: { label: "Transfer locked by registrant", type: "good" },
  serverTransferProhibited: { label: "Transfer locked by registry", type: "good" },
  clientDeleteProhibited: { label: "Deletion locked by registrant", type: "good" },
  serverDeleteProhibited: { label: "Deletion locked by registry", type: "good" },
  clientUpdateProhibited: { label: "Updates locked by registrant", type: "good" },
  serverUpdateProhibited: { label: "Updates locked by registry", type: "good" },
  clientHold: { label: "Domain suspended by registrar", type: "bad" },
  serverHold: { label: "Domain suspended by registry", type: "bad" },
  pendingDelete: { label: "Domain pending deletion", type: "warning" },
  pendingTransfer: { label: "Transfer in progress", type: "info" },
  redemptionPeriod: { label: "Domain in redemption period", type: "warning" },
  autoRenewPeriod: { label: "Auto-renewal period", type: "info" },
  addPeriod: { label: "Add grace period", type: "info" },
  renewPeriod: { label: "Renewal period", type: "info" },
  transferPeriod: { label: "Transfer period", type: "info" },
};

function StatusBadge({ status }: { status: string | Record<string, unknown> }) {
  // Handle both string and object status (Rust enum serialization)
  let statusStr: string;
  if (typeof status === "string") {
    statusStr = status;
  } else if (typeof status === "object" && status !== null) {
    // Handle Rust enum like { "Other": "custom" } or { "Ok": null }
    const keys = Object.keys(status);
    if (keys.length > 0) {
      const key = keys[0];
      const value = status[key];
      statusStr = value && typeof value === "string" ? value : key;
    } else {
      statusStr = "unknown";
    }
  } else {
    statusStr = String(status ?? "unknown");
  }

  const normalizedStatus = statusStr.toLowerCase().replace(/\s+/g, "");
  const info = Object.entries(STATUS_EXPLANATIONS).find(([key]) =>
    normalizedStatus.includes(key.toLowerCase())
  )?.[1];

  const typeColors = {
    good: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    warning: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    bad: "bg-red-500/10 text-red-400 border-red-500/30",
    info: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        info ? typeColors[info.type] : "bg-muted text-muted-foreground"
      )}
      title={info?.label}
    >
      {statusStr}
    </Badge>
  );
}

// Helper to convert status to string (handles Rust enum serialization)
function statusToString(status: string | Record<string, unknown>): string {
  if (typeof status === "string") {
    return status;
  } else if (typeof status === "object" && status !== null) {
    const keys = Object.keys(status);
    if (keys.length > 0) {
      const key = keys[0];
      const value = status[key];
      return value && typeof value === "string" ? value : key;
    }
  }
  return String(status ?? "");
}

// Security indicators
function SecurityIndicators({
  dnssec,
  privacyProtected,
  status,
}: {
  dnssec?: boolean;
  privacyProtected?: boolean;
  status?: Array<string | Record<string, unknown>>;
}) {
  const hasTransferLock = status?.some((s) => {
    const str = statusToString(s).toLowerCase();
    return str.includes("transferprohibited") || str.includes("transfer_prohibited");
  });
  const hasDeleteLock = status?.some((s) => {
    const str = statusToString(s).toLowerCase();
    return str.includes("deleteprohibited") || str.includes("delete_prohibited");
  });

  const indicators = [
    {
      label: "DNSSEC",
      enabled: dnssec,
      enabledIcon: ShieldCheck,
      disabledIcon: ShieldX,
      description: dnssec
        ? "DNS records are cryptographically signed"
        : "DNS records are not signed",
    },
    {
      label: "Privacy Protection",
      enabled: privacyProtected,
      enabledIcon: Eye,
      disabledIcon: EyeOff,
      description: privacyProtected
        ? "WHOIS contact info is protected"
        : "WHOIS contact info may be public",
    },
    {
      label: "Transfer Lock",
      enabled: hasTransferLock,
      enabledIcon: Lock,
      disabledIcon: Unlock,
      description: hasTransferLock
        ? "Domain transfers are prevented"
        : "Domain transfers are allowed",
    },
    {
      label: "Delete Lock",
      enabled: hasDeleteLock,
      enabledIcon: Lock,
      disabledIcon: Unlock,
      description: hasDeleteLock
        ? "Domain deletion is prevented"
        : "Domain deletion is allowed",
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {indicators.map((indicator) => {
        const Icon = indicator.enabled
          ? indicator.enabledIcon
          : indicator.disabledIcon;

        return (
          <div
            key={indicator.label}
            className={cn(
              "flex items-center gap-3 p-4 rounded-lg border",
              indicator.enabled
                ? "border-emerald-500/30 bg-emerald-500/5"
                : "border-border bg-muted/30"
            )}
          >
            <Icon
              className={cn(
                "h-5 w-5",
                indicator.enabled ? "text-emerald-400" : "text-muted-foreground"
              )}
            />
            <div>
              <p
                className={cn(
                  "font-medium text-sm",
                  indicator.enabled ? "text-emerald-400" : "text-muted-foreground"
                )}
              >
                {indicator.label}
              </p>
              <p className="text-xs text-muted-foreground">
                {indicator.description}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Convert issues to Issue objects
function normalizeIssues(
  issues?: Array<{ code?: string; message?: string } | string | Issue>
): Issue[] {
  if (!issues) return [];

  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "medium";
      let message = issue;

      // Parse severity prefix from backend (e.g., "[INFO] message")
      const prefixMatch = message.match(/^\[(INFO|WARNING|MEDIUM|HIGH|CRITICAL|LOW)\]\s*/i);
      if (prefixMatch) {
        const level = prefixMatch[1].toUpperCase();
        message = message.slice(prefixMatch[0].length);
        if (level === "INFO") severity = "info";
        else if (level === "LOW") severity = "low";
        else if (level === "MEDIUM" || level === "WARNING") severity = "medium";
        else if (level === "HIGH") severity = "high";
        else if (level === "CRITICAL") severity = "critical";
      } else if (
        message.toLowerCase().includes("expir") &&
        (message.toLowerCase().includes("30 days") ||
          message.toLowerCase().includes("critical"))
      ) {
        severity = "critical";
      } else if (
        message.toLowerCase().includes("expir") &&
        message.toLowerCase().includes("90 days")
      ) {
        severity = "high";
      } else if (message.toLowerCase().includes("warning")) {
        severity = "medium";
      }

      return {
        id: `whois-issue-${index}`,
        severity,
        message,
        module: "whois",
      };
    }

    if ("severity" in issue) {
      return issue as Issue;
    }

    return {
      id: `whois-issue-${index}`,
      severity: "medium" as Issue["severity"],
      message: issue.message || issue.code || "Unknown issue",
      code: issue.code,
      module: "whois",
    };
  });
}

export function WhoisModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: WhoisModuleViewProps) {
  const domainInfo = data.domain_info || {};
  const issues = normalizeIssues(data.issues);
  const age = domainAge(domainInfo.creation_date);

  return (
    <ModuleContainer
      moduleName="whois"
      icon={Shield}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Domain Overview */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* TLD */}
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                TLD
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                .{domainInfo.tld || "—"}
              </p>
            </CardContent>
          </Card>

          {/* Domain Age */}
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                Domain Age
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{age || "Unknown"}</p>
              {domainInfo.creation_date && (
                <p className="text-xs text-muted-foreground">
                  Created: {new Date(domainInfo.creation_date).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Last Updated */}
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                Last Updated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-mono">
                {domainInfo.updated_date
                  ? new Date(domainInfo.updated_date).toLocaleDateString()
                  : "Unknown"}
              </p>
            </CardContent>
          </Card>

          {/* Nameservers Count */}
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal uppercase tracking-wider">
                Nameservers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {domainInfo.name_servers?.length || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Expiration & Registrar */}
        <div className="grid gap-6 md:grid-cols-2">
          <ExpiryCountdown expiryDate={domainInfo.expiry_date} />
          <RegistrarCard registrar={domainInfo.registrar} />
        </div>

        {/* Status Codes */}
        {domainInfo.status && domainInfo.status.length > 0 && (
          <ModuleSection title="Domain Status" icon={Shield}>
            <div className="flex flex-wrap gap-2">
              {domainInfo.status.map((status, index) => (
                <StatusBadge key={index} status={status} />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Security Indicators */}
        <ModuleSection title="Security Indicators" icon={Lock}>
          <SecurityIndicators
            dnssec={domainInfo.dnssec}
            privacyProtected={domainInfo.privacy_protected}
            status={domainInfo.status}
          />
        </ModuleSection>

        {/* Nameservers */}
        {domainInfo.name_servers && domainInfo.name_servers.length > 0 && (
          <ModuleSection
            title="Nameservers"
            icon={Server}
            badge={
              <Badge variant="outline" className="text-xs">
                {domainInfo.name_servers.length}
              </Badge>
            }
          >
            <div className="space-y-2">
              {domainInfo.name_servers.map((ns, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border"
                >
                  <Server className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{ns}</span>
                </div>
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Issues"
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
              >
                {issues.length}
              </Badge>
            }
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

WhoisModuleView.displayName = "WhoisModuleView";
